import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

import { WatchlistData } from "./get-active-watchlist";

interface RequestParams {
  token: string;
  watchlistName: string;
  lang: Lang;
}

export const responseDataSchema = z.object({
  data: z.object({ watchlistId: z.string() }),
  message: z.string(),
});

export const addWatchlist = async ({
  token,
  watchlistName,
  lang,
}: RequestParams): Promise<z.infer<typeof responseDataSchema>> => {
  const endpoint = "/watchlist";
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-User-Profile": token,
      "Accept-Language": lang,
    },
    body: JSON.stringify({ watchlistName }),
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseDataSchema.parse(await res.json());
};

export const useAddWatchlist = () => {
  const { backendToken } = useAuth();
  const queryClient = useQueryClient();
  const { lang } = useLang();
  return useMutation(
    (params: Omit<RequestParams, "token" | "lang">) =>
      addWatchlist({ token: backendToken ?? "", lang, ...params }),
    {
      onMutate: async ({ watchlistName }) => {
        await queryClient.cancelQueries(["get-all-watchlists", backendToken]);

        const previousWatchlists = queryClient.getQueryData<WatchlistData[]>([
          "get-all-watchlists",
          backendToken,
          lang,
        ]);

        queryClient.setQueryData<WatchlistData[] | undefined>(
          ["get-all-watchlists", backendToken, lang],
          (old) =>
            old && [
              ...old,
              {
                watchlistName,
                symbols: [],
                active: false,
                // placeholders will be replaced after invalidation
                createdAt: "", // placeholder
                watchlistId: "", // placeholder
              },
            ]
        );

        return { previousWatchlists };
      },
      onError: (err, newData, context) => {
        queryClient.setQueryData(
          ["get-all-watchlists", backendToken, lang],
          context?.previousWatchlists
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["get-all-watchlists", backendToken, lang],
        });
      },
    }
  );
};
